import { Trans } from '@lingui/react/macro'
import { RevisionChangeCitations } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionChangeCitations'
import type {
  RevisionDetailEventRow,
  RevisionDiffDetailViewModel,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import { CHANGE_STATUS_COLORS } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionDiffOverlay'
import { revisionStatusLabel } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionStatusLegend'
import styled from 'styled-components'

const Root = styled.div`
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
`

const Verdict = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--foreground);
`

const SectionHeading = styled.h3`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
`

const SectionWhere = styled.span`
  margin-left: 0.5rem;
  color: var(--muted-foreground);
  font-size: 0.75rem;
  font-weight: 400;
`

const SectionText = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  white-space: pre-wrap;
`

const EventList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
`

const EventButton = styled.button<{ $selected: boolean }>`
  display: flex;
  width: 100%;
  cursor: pointer;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  border-radius: 0.25rem;
  background-color: ${(props) =>
    props.$selected ? 'var(--accent)' : 'transparent'};
  padding: 0.25rem 0.375rem;
  color: var(--foreground);
  font-size: 0.8125rem;
  text-align: left;

  &:hover {
    background-color: var(--accent);
  }
`

const StatusChip = styled.span<{ $fill: string; $stroke: string }>`
  flex-shrink: 0;
  border-radius: 0.25rem;
  border: 0.0625rem solid ${(props) => props.$stroke};
  background-color: ${(props) => props.$fill};
  padding: 0.0625rem 0.375rem;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
`

const EmptyMessage = styled.p`
  margin: 0;
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  font-style: italic;
`

type RevisionDetailChangeListProps = {
  viewModel: RevisionDiffDetailViewModel
  selectedEventId: string | undefined
  onSelectEvent: (event: RevisionDetailEventRow) => void
  fromImageUrl: string | undefined
  toImageUrl: string | undefined
}

export function RevisionDetailChangeList({
  viewModel,
  selectedEventId,
  onSelectEvent,
  fromImageUrl,
  toImageUrl,
}: RevisionDetailChangeListProps) {
  const { sections, unsectionedEvents, verdict } = viewModel

  const renderEvents = (events: readonly RevisionDetailEventRow[]) => (
    <EventList>
      {events.map((event) => {
        const colors = CHANGE_STATUS_COLORS[event.status]
        const selected = event.id === selectedEventId
        return (
          <li key={event.id}>
            <EventButton
              type='button'
              aria-pressed={selected}
              $selected={selected}
              onClick={() => onSelectEvent(event)}>
              <StatusChip $fill={colors.fill} $stroke={colors.stroke}>
                {revisionStatusLabel(event.status)}
              </StatusChip>
              {event.headline}
            </EventButton>
            {selected ? (
              <RevisionChangeCitations
                event={event}
                fromImageUrl={fromImageUrl}
                toImageUrl={toImageUrl}
              />
            ) : null}
          </li>
        )
      })}
    </EventList>
  )

  const hasContent =
    sections.length > 0 || unsectionedEvents.length > 0 || verdict !== null

  return (
    <Root>
      {verdict ? <Verdict>{verdict}</Verdict> : null}
      {sections.map((section, index) => (
        <div key={`${section.section}-${index}`}>
          <SectionHeading>
            {section.section}
            {section.where ? (
              <SectionWhere>{section.where}</SectionWhere>
            ) : null}
          </SectionHeading>
          <SectionText>{section.text}</SectionText>
          {renderEvents(section.events)}
        </div>
      ))}
      {unsectionedEvents.length > 0 ? (
        <div>
          <SectionHeading>
            <Trans>Other changes</Trans>
          </SectionHeading>
          {renderEvents(unsectionedEvents)}
        </div>
      ) : null}
      {hasContent ? null : (
        <EmptyMessage>
          <Trans>No detailed changes</Trans>
        </EmptyMessage>
      )}
    </Root>
  )
}
